import { Auth, Config } from 'olympe'
import { v4 as uuidv4 } from 'uuid'
import { toBase64 } from '@olympeio/core';
import { SFFilterType } from '../shared/_filters';
import { getUserPlants } from '../shared/_helpers';

/**
 * @typedef HttpProxyError
 * @type {Object}
 * @property {number} statusCode
 * @property {string} error
 * @property {string} message
 */

/**
 * @param {HttpProxyError} error
 * @param {string=} customText
 * @return {string}
 */
export const formatErrorMessage = (error, customText = 'Error') => {
    if (error?.statusCode >= 400) {
        const httpErrorText = error.error || 'Error';
        return `${customText} : ${error.statusCode} ${httpErrorText}, ${error.message}`;
    } else {
        return `${customText} : ${error.message}`;
    }
}

/**
  * @param {Array} data
  * @param {string} operation
  * @return {Object | HttpProxyError}
  */
export const updateNoBcp = async (data, operation) => {
    const rootUrl = Config.getParameter('nobcp_update_path')
    const access_token = sessionStorage.getItem("ms_auth_token");
    const proxyUrl = Config.getParameter('http_proxy_path')

    const body = prepareBody(rootUrl, data, operation)

    try {
        const request = await fetch(proxyUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${access_token}`,
            },
            body: JSON.stringify(body),
        })
        if (!request.ok) {
            // return error object returned by http proxy
            return await request.json();
        }

        // Fix for the 204 response from the Delete, we check first if we received something.
        let result = await request.text()
        return result.length ? JSON.parse(result) : { error: false }
    } catch (error) {
        // catches code errors and error where httpProxy did not respond
        return {
            statusCode: 1,
            message: error.message,
        }
    }
}

const prepareBody = (rootUrl, data, operation) => {
    const access_token = sessionStorage.getItem("ms_auth_token");
    let body = {
        destination: rootUrl,
        token: Auth.getUserToken().nonce,
        opperation: operation === 'DELETE' ? 'DELETE' : 'PUT',
        headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${access_token}`,
        },
        payload: {
            'event-date-time': new Date().toISOString(),
            'transaction-id': uuidv4(),
            'bcp-records': [],
        },
    }
    data.map(record => {
        body.payload['bcp-records'].push({
            plantId: record.plantId,
            // Below fields doesn't accept null/undefined values, hence assigning whitespace
            comments: record.comments ?? "",
            expiry: record.expiry ?? "",
            materialId: record.materialId,
            materialType: record.materialType,
            'modified-by': {
                user_id: localStorage.getItem('userName'),
                full_name: localStorage.getItem('userFullName'),
                email: localStorage.getItem('userEmail'),
            },
            reasonCode: record.reasonCode,
            specificationCode: record.specificationCode,
            vendorSiteNumber: record.vendorSiteNumber,
            document: {
                fileID: record.document.fileID,
                fileName: record.document.fileName,
                fileExt: record.document.fileExt,
            }
        })
    })
    return body
}

/**
 *
 * @param {File} file
 * @return {Promise<Object | HttpProxyError>}
 */
export const uploadFile = async (file) => {
    const proxyUrl = Config.getParameter('http_proxy_path');
    const rootUrl = Config.getParameter('upload_file_path');
    const access_token = sessionStorage.getItem("ms_auth_token");

    const base64File = toBase64(await file.arrayBuffer());

    // split name and extension
    const result = /(.+)\.(.+)$/.exec(file.name);
    let fileName, extension;
    if (result !== null) {
        fileName = result[1];
        extension = result[2];
    } else {
        fileName = file.name;
        extension = '';
    }

    let body = {
        destination: rootUrl,
        token: Auth.getUserToken().nonce,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${access_token}`
        },
        opperation: 'POST',
        payload: {
            fileName: fileName,
            fileExt: extension,
            fileContents: base64File,
        },
    }

    try {
        const request = await fetch(proxyUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${access_token}`
            },
            body: JSON.stringify(body),
        })
        if (!request.ok) {
            return await request.json();
        }

        let result = await request.text()
        return result.length ? JSON.parse(result) : { error: false }
    } catch (error) {
        return {
            statusCode: 1,
            message: error.message,
        }
    }
}

/**
 * Check if the row has a new file to upload, and update the row data with the new file information
 * after it has been uploaded.
 *
 * Return null if successful, and the error message otherwise.
 *
 * @param {Object} row
 * @return {Promise<string?>}
 */
export const uploadFileForRow = async (row) => {
    if (row['reasonCode'] === 'RF') {
        if (row['newUploadedFile'] !== undefined) {
            const result = await uploadFile(row['newUploadedFile']);
            if (!result.error && result['id'] !== undefined) {
                // update document info in material row for PUT request
                row['document'] = {
                    fileID: result['id'],
                    fileName: result['fileName'],
                    fileExt: result['fileExt'],
                };
                delete row['newUploadedFile'];
            } else {
                // TODO review returning string directly
                return formatErrorMessage(result, 'An error occurred while updating the record');
            }
        }
        // else, no new file to upload, leave file info as is
    } else {
        // clear document info in material row for PUT request
        row['document'] = {
            fileID: "",
            fileName: "",
            fileExt: "",
        };
    }
    return null;
}

/**
 * Upload a single file, then assign the uploaded file to all given rows
 *
 * Return null if successful, and the error message otherwise.
 *
 * @param {Array<Object>} rows
 * @param {File} newFile
 * @return {Promise<string?>}
 */
export const uploadFileForMultipleRows = async (rows, newFile) => {
    const result = await uploadFile(newFile);
    if (result.statusCode === undefined && result['id'] !== undefined) {
        rows.forEach((row) => {
            // update document info in material row for PUT request
            row['document'] = {
                fileID: result['id'],
                fileName: result['fileName'],
                fileExt: result['fileExt'],
            }
        });
    } else {
        // TODO review returning string directly
        return formatErrorMessage(result, 'An error occurred while updating the record');
    }
    return null;
}

/**
 * @param {Array<Object>} rows
 */
export const clearFileForAllRows = (rows) => {
    rows.forEach((row) => {
        // update document info in material row for PUT request
        row['document'] = {
            fileID: "",
            fileName: "",
            fileExt: "",
        }
    });
}

export const downloadFile = async (fileId, fileName, fileExt) => {
    const proxyUrl = Config.getParameter('http_proxy_path');
    const rootUrl = Config.getParameter('download_file_path');
    const access_token = sessionStorage.getItem("ms_auth_token");
    let body = {
        destination: rootUrl,
        token: Auth.getUserToken().nonce,
        headers: { 'Content-Type': 'application/json',
        'Authorization': `Bearer ${access_token}`
     },
        opperation: 'POST',
        payload: {
            fileId: fileId,
            fileName: fileName,
            fileExt: fileExt.trim(),
        },
    }

    try {
        const request = await fetch(proxyUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${access_token}`
            },
            body: JSON.stringify(body),
        })
        if (!request.ok) {
            return await request.json();
        }

        let result = await request.text()
        return result.length ? JSON.parse(result) : { success: false }
    } catch (error) {
        return {
            statusCode: 1,
            message: error.message,
        }
    }
}

/**
 *
 * @param {number} page
 * @param {number} pageSize
 * @return {Object} result
 */
export const noBcpGetPage = async (nextCursor, pageSize, filters) => {
    const rootUrl = Config.getParameter('nobcp_path')
    const proxyUrl = Config.getParameter('http_proxy_path')
    //const proxyUrl = 'http://localhost:5000/proxy/forward'
    let startIndex = nextCursor ? nextCursor : 0
    let endIndex = startIndex + pageSize

    const userPlants = getUserPlants().map(item => item.plantID);
    const access_token = sessionStorage.getItem("ms_auth_token");
    console.log("::: noBcpGetPage: access_token ::::", access_token)
    // Calculate the offset

    let body = {
        destination: rootUrl,
        token: Auth.getUserToken().nonce,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${access_token}`
        },
        opperation: 'POST',
        payload: {
            offset: startIndex.toString(),
            limit: endIndex.toString(),
            plants: userPlants, // by default, if no filter on plants is set, we pick all the plants the user has access to
        },
    }

    if (filters) {
        if (filters[SFFilterType.PLANT.key]) body.payload.plants = filters[SFFilterType.PLANT.key]
        if (filters[SFFilterType.MATERIAL.key]) body.payload.materials = filters[SFFilterType.MATERIAL.key]
        if (filters[SFFilterType.MATERIAL_TYPE.key]) body.payload.materialType = filters[SFFilterType.MATERIAL_TYPE.key]
        if (filters[SFFilterType.SPECIFICATION.key]) body.payload.specifications = filters[SFFilterType.SPECIFICATION.key]
        if (filters.markets) body.payload.market = filters.markets
        if (filters[SFFilterType.REASON_CODE.key]) body.payload.reasonCode = filters[SFFilterType.REASON_CODE.key]
        if (filters[SFFilterType.MODIFIED_BY.key]) body.payload['modified-by'] = filters[SFFilterType.MODIFIED_BY.key]
        if (filters[SFFilterType.SPEND_CAT_L1.key]) body.payload.spendCategory1 = filters[SFFilterType.SPEND_CAT_L1.key]
        if (filters[SFFilterType.MATERIAL_GROUP.key]) body.payload.materialGroup = filters[SFFilterType.MATERIAL_GROUP.key]
        if (filters[SFFilterType.NNS.key]) body.payload.nns = filters[SFFilterType.NNS.key]
        if (filters[SFFilterType.PS_NAME.key]) body.payload.psName = filters[SFFilterType.PS_NAME.key]
        if (filters[SFFilterType.PM_NAME.key]) body.payload.pmName = filters[SFFilterType.PM_NAME.key]
        if (filters[SFFilterType.TASTY_CLASSIFICATION.key]) body.payload.tastyClassification = filters[SFFilterType.TASTY_CLASSIFICATION.key]
    }

    try {
        const request = await fetch(proxyUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${access_token}`
            },
            body: JSON.stringify(body),
        })

        let result = await request.json()
        if (!request.ok) {
            return result;
        }

        if (result.materials !== null && (result.materials.length) === (endIndex - startIndex)) {
            result.nextCursor = endIndex
        } else {
            result.nextCursor = null
        }
        return result
    } catch (error) {
        return {
            statusCode: 1,
            message: error.message,
        }
    }
}
